import React, { useState } from 'react';
import api from '../../../services/api/axiosConfig'; // Correct path to your configured axios instance
import errorHandler from '../../../utils/errorHandler'; // Your global error handler utility
import { Spinner, Alert, Progress } from 'flowbite-react'; // Import Flowbite components for UI feedback
import { FaTimesCircle, FaCheckCircle } from 'react-icons/fa'; // Icons for alerts
import Modal from '../../../components/Modal';
// Generic Modal Component (dependency)


// Mock Label component (dependency) - Kept as is
const Label = ({ htmlFor, label }) => {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
  );
};

// Mock ShowValidationMessage component (dependency) - Kept as is
const ShowValidationMessage = ({ error, id }) => {
  return (
    error ? <p id={id} className="mt-1 text-sm text-red-600">{error}</p> : null
  );
};

// The UpdateCompanyLogoModal component
const UpdateCompanyLogoModal = ({ isOpen, onClose, onUpdateSuccess }) => {
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Added for progress tracking
  const [uploadError, setUploadError] = useState(null);    // Added for general upload errors
  const [uploadSuccess, setUploadSuccess] = useState(null); // Added for general upload success

  const handleImageChange = (e) => {
    setErrors(prev => ({ ...prev, company_logo: '' })); // Clear field error
    setUploadError(null); // Clear general upload error
    setUploadSuccess(null); // Clear previous success message
    setUploadProgress(0); // Reset progress

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, company_logo: 'Please select a valid image file (e.g., .jpg, .png, .gif).' }));
        setProfileImageFile(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // Example: 5MB limit
        setErrors(prev => ({ ...prev, company_logo: 'File size exceeds 5MB limit. Max 5MB allowed.' }));
        setProfileImageFile(null);
        return;
      }

      setProfileImageFile(file);
    } else {
      setProfileImageFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // Clear previous field errors
    setUploadError(null); // Clear previous general error
    setUploadSuccess(null); // Clear previous success message

    if (!profileImageFile) {
      setErrors(prev => ({ ...prev, company_logo: 'Please select a logo file to upload.' }));
      return;
    }

    setLoading(true); // Start loading state
    setUploadProgress(0); // Reset progress at the start of submission

    const formData = new FormData();
    formData.append('company_logo', profileImageFile);

    try {
      const response = await api.post('/update-company-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // Explicitly set for file uploads
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },

        
      });

      if (response.data.success) {
        console.log("Company Logo Updated Successfully:", response.data.logo_url);
        setUploadSuccess('Company logo updated successfully!'); // Set success message
       
        setProfileImageFile(null); // Clear selected file
        setUploadProgress(100); // Ensure progress reaches 100% on success
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        
      } else {
        // If API returns success: false but with a message
        setUploadError(response.data.message || 'Failed to upload logo.');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadSuccess(null); // Clear any lingering success message
      // Use your existing errorHandler for field-specific errors,
      // and set a general uploadError if errorHandler doesn't cover it.
      errorHandler(error, setErrors, (generalErrorMessage) => {
          setUploadError(generalErrorMessage); // Set general error from errorHandler
      });
      // Fallback if errorHandler doesn't provide a general message or field errors
      if (!errors.company_logo && !uploadError) {
          setUploadError('An unexpected error occurred during upload. Please try again.');
      }
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Company Logo">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Display general upload error alert */}
        {uploadError && (
          <Alert color="failure" onDismiss={() => setUploadError(null)} className="mb-3">
            <FaTimesCircle className="mr-2" /> {uploadError}
          </Alert>
        )}
        {/* Display general upload success alert */}
        {uploadSuccess && (
          <Alert color="success" onDismiss={() => setUploadSuccess(null)} className="mb-3">
            <FaCheckCircle className="mr-2" /> {uploadSuccess}
          </Alert>
        )}

        <div>
          <Label htmlFor="company_logo" label="Company Logo" />
          <input
            id="company_logo"
            name="company_logo"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            // Slightly adjusted class for visual consistency with Flowbite TextInput
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 border border-gray-300 rounded-md shadow-sm focus:border-green-500 focus:ring-green-500"
            aria-invalid={errors.company_logo ? "true" : "false"}
            aria-describedby="company-logo-error"
          />
          <ShowValidationMessage error={errors.company_logo} id="company-logo-error" />

          {profileImageFile && (
            <div className="mt-4">
              <p className="text-sm text-gray-700">Selected file: <span className="font-medium">{profileImageFile.name}</span></p>
              <img
                src={URL.createObjectURL(profileImageFile)}
                alt="Company Logo Preview"
                className="mt-2 h-32 w-32 object-contain rounded-md border border-gray-300"
              />
            </div>
          )}
        </div>

        {/* Upload Progress Bar */}
        {loading && uploadProgress > 0 && uploadProgress < 100 && (
          <div className="w-full mb-4">
            <Progress progress={uploadProgress} textLabel="Uploading" size="lg" color="blue" />
            <span className="text-sm text-gray-500 mt-1 block">
              {uploadProgress === 100 ? 'Processing...' : `Upload progress: ${uploadProgress}%`}
            </span>
          </div>
        )}
        {/* Show processing message after upload completes but before success/error is confirmed */}
        {loading && uploadProgress === 100 && !uploadSuccess && !uploadError && (
            <div className="w-full mb-4">
                <p className="text-sm text-gray-500 text-center">Processing file...</p>
            </div>
        )}

        <button
          type="submit"
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
          disabled={!profileImageFile || loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {uploadProgress > 0 && uploadProgress < 100 ? `Uploading (${uploadProgress}%)` : 'Uploading...'}
            </>
          ) : 'Upload Logo'}
        </button>
      </form>
    </Modal>
  );
};

export default UpdateCompanyLogoModal;
